import React, { useEffect, useState } from 'react'
import { Image, ScrollView, TouchableOpacity, View } from 'react-native'
import styled from 'styled-components'
import { colors } from 'infra/color'
import { ColumnLayout, RowLayout } from 'ui/common/layout'
import { Body1, Body2, Caption } from 'ui/common/text'
import { alertError, getSecureImageUrl } from 'infra/util'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useNavigation } from '@react-navigation/native'
import { TopNavigationBar } from 'ui/common/top-navigation-bar'
import { PROFILE_IMG_PLACEHOLDER_IMAGE } from 'image/icon'
import { observer } from 'mobx-react'
import { useMe, useUserDetail } from 'api/hooks'
import { updateUserProfile } from 'api/writes'
import { mutate } from 'swr'
import { launchImageLibrary } from 'react-native-image-picker'
import Ionicons from 'react-native-vector-icons/Ionicons'
import { CustomTextInput } from 'ui/common/custom-text-input'

export const UserMeEditScreen = observer(() => {
  const insets = useSafeAreaInsets()
  const navigation = useNavigation()

  const { data: me } = useMe()
  const { data: userDetail } = useUserDetail(me?.id)

  const [nickname, setNickname] = useState('')
  const [bio, setBio] = useState('')
  const [instagramId, setInstagramId] = useState('')
  const [profileImage, setProfileImage] = useState<{
    uri: string
    name?: string
    type?: string
  } | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (userDetail) {
      setNickname(userDetail.nickname || '')
      setBio(userDetail.bio || '')
      setInstagramId(userDetail.instagramId || '')
    }
  }, [userDetail])

  const handlePickImage = async () => {
    try {
      const result = await launchImageLibrary({
        mediaType: 'photo',
        quality: 0.8,
        maxWidth: 1024,
        maxHeight: 1024,
      })

      if (result.didCancel) return

      if (result.errorCode) {
        alertError(result.errorMessage || '이미지를 불러오는데 실패했습니다.')
        return
      }

      const asset = result.assets?.[0]
      if (!asset?.uri) {
        alertError('선택한 이미지를 불러오는데 실패했습니다.')
        return
      }

      setProfileImage({
        uri: asset.uri,
        name: asset.fileName || 'profile.jpg',
        type: asset.type || 'image/jpeg',
      })
    } catch (e) {
      alertError(e)
    }
  }

  const handleSave = async () => {
    if (isLoading) return

    if (!nickname.trim()) {
      alertError('닉네임을 입력해 주세요.')
      return
    }

    setIsLoading(true)
    try {
      await updateUserProfile({
        nickname: nickname.trim(),
        bio: bio.trim(),
        instagramId: instagramId.trim(),
        image: profileImage
          ? {
              uri: profileImage.uri,
              name: profileImage.name || 'image.jpg',
              type: profileImage.type || 'image/jpeg',
            }
          : undefined,
      })

      const cacheKeys = ['/users/me']
      if (me?.id) cacheKeys.push(`/users/${me.id}`)
      await Promise.allSettled(cacheKeys.map((key) => mutate(key)))

      navigation.goBack()
    } catch (e) {
      alertError(e)
    } finally {
      setIsLoading(false)
    }
  }

  const currentProfileImage = profileImage
    ? { uri: profileImage.uri }
    : userDetail?.thumbnailImageUrl
    ? { uri: getSecureImageUrl(userDetail.thumbnailImageUrl) }
    : PROFILE_IMG_PLACEHOLDER_IMAGE

  return (
    <Container style={{ paddingTop: insets.top }}>
      <TopNavigationBar title='프로필 편집'>
        <RowLayout
          style={{ flex: 1, justifyContent: 'flex-end', paddingRight: 16 }}
        >
          <TouchableOpacity onPress={handleSave} disabled={isLoading}>
            <Body1
              style={{
                fontWeight: 'bold',
                color: isLoading ? colors.gray.v400 : colors.primary,
              }}
            >
              완료
            </Body1>
          </TouchableOpacity>
        </RowLayout>
      </TopNavigationBar>

      <ScrollView>
        {/* Profile Image */}
        <ImageSection>
          <TouchableOpacity onPress={handlePickImage} activeOpacity={0.8}>
            <ImageContainer>
              <ProfileImage source={currentProfileImage} />
              <EditBadge>
                <Ionicons name='camera' size={16} color={colors.white} />
              </EditBadge>
            </ImageContainer>
          </TouchableOpacity>
        </ImageSection>

        {/* Form */}
        <FormRow
          style={{ borderTopWidth: 1, borderTopColor: colors.gray.v100 }}
        >
          <FormLabel>닉네임</FormLabel>
          <StyledTextInput
            value={nickname}
            onChangeText={setNickname}
            placeholder='닉네임'
            maxLength={20}
          />
          <Caption style={{ color: colors.gray.v400, marginLeft: 8 }}>
            {nickname.length}/20
          </Caption>
        </FormRow>

        <FormRow style={{ alignItems: 'flex-start' }}>
          <FormLabel style={{ marginTop: 12 }}>자기소개</FormLabel>
          <ColumnLayout style={{ flex: 1 }}>
            <StyledTextInput
              value={bio}
              onChangeText={setBio}
              placeholder='자기소개'
              multiline
              numberOfLines={3}
              maxLength={100}
              style={{ height: 80, textAlignVertical: 'top', paddingTop: 12 }}
            />
            <Caption
              style={{
                textAlign: 'right',
                color: colors.gray.v400,
                marginTop: 4,
              }}
            >
              {bio.length}/100
            </Caption>
          </ColumnLayout>
        </FormRow>

        <FormRow>
          <FormLabel>인스타그램</FormLabel>
          <RowLayout style={{ flex: 1, alignItems: 'center' }}>
            <Body2 style={{ color: colors.gray.v600, marginRight: 2 }}>@</Body2>
            <StyledTextInput
              value={instagramId}
              onChangeText={setInstagramId}
              placeholder='사용자 이름'
              autoCapitalize='none'
            />
          </RowLayout>
        </FormRow>
      </ScrollView>
    </Container>
  )
})

const Container = styled(ColumnLayout)`
  flex: 1;
  background-color: ${colors.white};
`

const ImageSection = styled(View)`
  align-items: center;
  margin-top: 16px;
  margin-bottom: 36px;
`

const ImageContainer = styled(View)`
  position: relative;
`

const ProfileImage = styled(Image)`
  width: 90px;
  height: 90px;
  border-radius: 45px;
  background-color: ${colors.gray.v200};
`

const EditBadge = styled(View)`
  position: absolute;
  bottom: 0;
  right: 0;
  background-color: ${colors.black};
  width: 30px;
  height: 30px;
  border-radius: 15px;
  align-items: center;
  justify-content: center;
  border-width: 2px;
  border-color: ${colors.white};
`

const FormRow = styled(RowLayout)`
  align-items: center;
  padding: 16px;
  border-bottom-width: 1px;
  border-bottom-color: ${colors.gray.v100};
`

const FormLabel = styled(Body1)`
  width: 100px;
  color: ${colors.black};
  font-weight: 500;
`

const StyledTextInput = styled(CustomTextInput).attrs({
  placeholderTextColor: colors.gray.v400,
})`
  flex: 1;
  padding: 0;
  font-size: 16px;
  color: ${colors.black};
`
